// frontend/src/ProtectedElement.js
// Generic wrapper to enforce permission-controlled UI rendering/behavior.
import React from 'react';
import { usePermissions } from './PermissionsContext';
import './ProtectedElement.css';

/**
 * ProtectedElement
 * Props:
 * - required (string | string[]) single or multiple permissions (AND logic by default if array)
 * - any (string | string[]) optional: treat as ANY-of set (OR logic). If provided, 'required' still must all pass.
 * - behavior: 'hide' | 'disable' (default 'hide')
 * - fallback: ReactNode custom fallback (if behavior === 'hide' and no fallbackVariant)
 * - fallbackVariant: 'message' | 'overlay' (built-in consistent denied styles)
 *     'message' : Replaces content with a styled permission denied panel.
 *     'overlay' : Renders children dimmed with a grey overlay reading "Feature Disabled".
 * - deniedTitle: heading text for the message variant (default: 'Permission denied')
 * - deniedDescription: description line under title. If omitted a default sentence is shown.
 * - contactEmail: email inserted into default description (default: 'support@example.com').
 * - overlayLabel: center label for overlay variant (default: 'Feature Disabled').
 * - strategy: 'all' | 'any' when passing array via 'required' (default 'all')
 * - disabledTooltip: tooltip text when disabled
 * - passThroughIfLoading: if true, renders children while permissions are still loading
 */
export const ProtectedElement = ({
  required,
  any,
  behavior = 'hide',
  behaviour,
  strategy = 'all',
  fallback = null,
  fallbackVariant = null, // 'message' | 'overlay'
  deniedTitle = 'Permission denied',
  deniedDescription,
  contactEmail = 'support@example.com',
  overlayLabel = 'Feature Disabled',
  disabledTooltip = 'Permission denied',
  fallbackMinHeight = 0, // ensures some space when no children given
  forceFullHeight = false, // new: force wrapper to take 100% height of parent
  children,
  passThroughIfLoading = false
}) => {
  const { hasPermission, hasAllPermissions, hasAnyPermission, loading } = usePermissions();

  // Normalize supported spellings/values.
  // - Some call sites use british spelling `behaviour`
  // - Some call sites use `disabled` but the component expects `disable`
  const normalizedBehavior = (() => {
    const b = (behaviour ?? behavior);
    if (b === 'disabled') return 'disable';
    return b;
  })();

  const requiredArray = Array.isArray(required) ? required : (required ? [required] : []);
  const anyArray = Array.isArray(any) ? any : (any ? [any] : []);

  const requiredOk = strategy === 'any' ? hasAnyPermission(requiredArray) : hasAllPermissions(requiredArray);
  const anyOk = anyArray.length === 0 ? true : hasAnyPermission(anyArray);
  const allowed = requiredOk && anyOk;

  if (loading && passThroughIfLoading) return <>{children}</>;

  if (allowed) return <>{children}</>;

  // Built-in fallback variants have priority over basic hide fallback (except when behavior === 'disable')
  if (normalizedBehavior === 'disable' && !fallbackVariant) {
    try {
      const child = React.Children.only(children);
      return React.cloneElement(child, {
        disabled: true,
        'aria-disabled': true,
        title: child.props?.title ? `${child.props.title} (No permission)` : disabledTooltip,
        className: `${child.props.className || ''} perm-disabled`.trim()
      });
    } catch (e) {
      // If multiple children, fall back to hiding.
      return null;
    }
  }
  // Handle standardized variants if provided.
  if (fallbackVariant === 'message') {
    const desc = deniedDescription || `You do not have the required permissions to view this feature. If you believe this is a mistake, please contact your administrator or email ${contactEmail}.`;
    // Preserve layout: render children invisibly to capture natural size, then absolutely stack message.
    return (
      <div className="perm-message-wrapper" style={fallbackMinHeight ? { minHeight: fallbackMinHeight } : undefined}>
        {children && (
          <div className="perm-message-placeholder" aria-hidden="true">
            {children}
          </div>
        )}
        <div className="perm-message-foreground" role="alert" aria-live="polite">
          <div className="perm-denied-message">
            <div className="perm-denied-message__title">{deniedTitle}</div>
            <div className="perm-denied-message__body">{desc}</div>
          </div>
        </div>
      </div>
    );
  }

  if (fallbackVariant === 'overlay') {
    // New overlay strategy (2025-10):
    // Goals:
    //  - Preserve EXACT layout/size of wrapped content (no reflow changes)
    //  - Dim + blur underlying content while allowing native scrolling
    //  - Keep a centered message that stays fixed relative to the viewport while user scrolls
    //  - Avoid pointer interaction with underlying interactive elements, but DO allow scroll/selection scrollbars
    //  - Work with any child type: single DOM node, composite component, fragments, multiple children

    const styleMin = !children && fallbackMinHeight ? { minHeight: fallbackMinHeight } : undefined;
    const childArray = React.Children.toArray(children);
    const singleChild = childArray.length === 1 ? childArray[0] : null;

    // We keep original class/style of a single root DOM element on an inner container to avoid wrapper styling collisions.
    // Outer wrapper establishes positioning & overlay layers but should not alter sizing context.
    let contentNode;
    if (!children) {
      contentNode = <div className="perm-overlay-base perm-overlay-spacer" />;
    } else if (singleChild && React.isValidElement(singleChild) && typeof singleChild.type === 'string') {
      // Plain DOM element: replicate its props onto an inner container so we don't need to clone
      const { className = '', style = {}, children: innerKids, ...rest } = singleChild.props;
      contentNode = (
        <div className={`perm-overlay-base ${className}`.trim()} style={style} {...rest} aria-hidden={false}>
          {innerKids}
        </div>
      );
    } else if (singleChild && singleChild.type === React.Fragment) {
      contentNode = <div className="perm-overlay-base">{singleChild.props.children}</div>;
    } else {
      // Multiple or composite component(s)
      contentNode = <div className="perm-overlay-base">{children}</div>;
    }

    return (
      <div
        className={`perm-overlay-wrapper flex-pass ${forceFullHeight ? 'full-height' : ''}`.trim()}
        data-permission-state="denied"
        style={styleMin}
      >
        {/* Underlying scrollable content (dimmed via CSS) */}
        {contentNode}
        {/* Dim & blur layer pinned to content bounds (non-interactive) */}
        <div className="perm-overlay-dim" aria-hidden="true" />
        {/* Fixed viewport-centered label; separate so that scrolling content moves underneath */}
        <div className="perm-overlay-center" role="alert" aria-live="polite">
          <span className="perm-overlay-label">{overlayLabel}</span>
        </div>
      </div>
    );
  }

  // Custom fallback node support
  return fallback ? <>{fallback}</> : null;
};

export default ProtectedElement;
